import React, { useState, useRef, useEffect, useContext, useCallback } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  Row,
  Col,
  DatePicker,
  Typography,
  Tabs,
  Dropdown,
  Modal,
  message,
  Checkbox,
  Empty,
  Spin
} from 'antd'
import {
  CalendarOutlined,
  ExportOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  WalletOutlined,
  SettingOutlined,
  DeleteOutlined,
  CloseOutlined,
  MoreOutlined
} from '@ant-design/icons'
import Chart from 'chart.js/auto'

// Components
import Button from '../../components/common/button/button'
import Transactions from './transactions/transactions'
import CashCount from './cashCount/cashCount'
import TransactionModal from './transactions/transactionModal/transactionModal'
import CashCountModal from './cashCount/cashCountModal/cashCountModal'
import DeletedTransactionsModal from './transactions/deletedTransactionsModal'
import DenominationModal from '../../components/feature/denominationModal/denominationModal'

import { StoreContext } from '../../context/storeContext'

// API
import { closeMonthlyBalance, fetchCashCounts } from '../../api/cashCountApiService/cashCountApiUtils'
import { useMsal } from '@azure/msal-react'

// Utils
import { formatCurrency, exportTransactionsAsCSV, exportTransactionsAsPDF, exportChartsAsPDF } from './pettCashUtils'
import { getCachedUserAccessData } from '../../utils/appUtils'

// Styles
import './pettyCash.scss'

// Constants
const MANILA_TIMEZONE = 'Asia/Manila'
const DEFAULT_COLUMNS = [
  { key: 'transactionId', title: 'Transaction ID', checked: true },
  { key: 'date', title: 'Invoice Date and Time', checked: true },
  { key: 'createdAt', title: 'Created At', checked: true },
  { key: 'description', title: 'Description', checked: true },
  { key: 'expenseType', title: 'Expense/Deposit Type', checked: true },
  { key: 'createdBy', title: 'Created By', checked: true },
  { key: 'transactionApprover', title: 'Actioned By', checked: true },
  { key: 'status', title: 'Status', checked: true },
  { key: 'actions', title: 'Actions', checked: true }
]

// Extend dayjs
dayjs.extend(utc)
dayjs.extend(timezone)

const { Title } = Typography

// Utility functions
const formatCurrencyWithDecimals = (value) => {
  if (value === null || value === undefined) return ''
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const createExportData = (transactions) =>
  transactions.map(item => ({
    'Sr. No.': item.srNo,
    'Transaction ID': item.transactionId,
    'Invoice Number': item.invoiceNumber,
    'Date & Time': `${item.date} ${item.time}`,
    'Description': item.description,
    'Type': item.expenseType,
    'Created By': item.createdBy || 'N/A',
    'Cash Out': item.cashOut ? formatCurrency(item.cashOut, true) : '',
    'Cash In': item.cashIn ? formatCurrency(item.cashIn, true) : '',
    'Balance': item.runningBalance !== undefined ? formatCurrency(item.runningBalance, true) : ''
  }))

// Custom hooks
const usePermissions = () => {
  const getUserAccessPermissions = () => {
    try {
      const userAccessData = getCachedUserAccessData()
      if (userAccessData && userAccessData.message && userAccessData.message.permissions) {
        return userAccessData.message.permissions.pettyCash || {}
      }
      return {}
    } catch (error) {
      console.error('Error getting user access permissions:', error)
      return {}
    }
  }

  const permissions = getUserAccessPermissions()

  return {
    canRead: permissions.read === true,
    canWrite: permissions.write === true,
    canCloseMonth: permissions.closeMonth === true,
    hasAccess: permissions.access === true
  }
}

const useTimeHelpers = (selectedDate) => {
  const isCurrentMonthLastDay = useCallback(() => {
    const nowInManila = dayjs().tz(MANILA_TIMEZONE)
    const selectedDateInManila = dayjs(selectedDate).tz(MANILA_TIMEZONE)

    const isCurrentMonthYear = selectedDateInManila.isSame(nowInManila, 'month') &&
      selectedDateInManila.isSame(nowInManila, 'year')

    if (!isCurrentMonthYear) return false

    const lastDayOfMonth = nowInManila.endOf('month').date()
    const todayInManila = nowInManila.date()

    // Allow closing 5 days before the end of the month
    const daysBeforeMonthEnd = lastDayOfMonth - todayInManila
    return daysBeforeMonthEnd <= 5

  }, [selectedDate])

  const isPreviousMonth = useCallback(() => {
    const nowInManila = dayjs().tz(MANILA_TIMEZONE)
    const selectedDateInManila = dayjs(selectedDate).tz(MANILA_TIMEZONE)
    return selectedDateInManila.isBefore(nowInManila, 'month')
  }, [selectedDate])

  return { isCurrentMonthLastDay, isPreviousMonth }
}

const PettyCashManagement = () => {
  // Refs
  const transactionsRef = useRef(null)
  const cashCountRef = useRef(null)
  const pieChartRef = useRef(null)
  const barChartRef = useRef(null)
  const popupPieChartRef = useRef(null)

  // Context
  const { instance } = useMsal()
  const storeContext = useContext(StoreContext)
  const currentStoreName = storeContext?.name || 'Unknown Store'

  // Permissions
  const { canWrite, canCloseMonth, hasAccess } = usePermissions()

  // Early return if no access
  if (!hasAccess) {
    return (
      <div className="petty-cash-container">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Empty
            description="You don't have access to Petty Cash Management. Please contact your administrator."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    )
  }

  // State
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [activeTab, setActiveTab] = useState('all')
  const [metricsData, setMetricsData] = useState({
    systemCashOnHand: 0,
    actualCashOnHand: 0,
    openingBalance: 0,
    totalExpenses: 0,
    totalReplenishment: 0
  })
  const [chartModalVisible, setChartModalVisible] = useState(false)
  const [expenseByType, setExpenseByType] = useState({})

  // Modal states
  const [transactionModalVisible, setTransactionModalVisible] = useState(false)
  const [cashCountModalVisible, setCashCountModalVisible] = useState(false)
  const [closeMonthModalVisible, setCloseMonthModalVisible] = useState(false)
  const [denominationModalVisible, setDenominationModalVisible] = useState(false)
  const [columnChooserVisible, setColumnChooserVisible] = useState(false)
  const [deletedLogsVisible, setDeletedLogsVisible] = useState(false)

  // Data states
  const [hasTransactions, setHasTransactions] = useState(true)
  const [hasCashCountHistory, setHasCashCountHistory] = useState(false)
  const [cashCountDataLoaded, setCashCountDataLoaded] = useState(false)
  const [latestCashCount, setLatestCashCount] = useState(null)
  const [backendMonthClosedStatus, setBackendMonthClosedStatus] = useState(false)
  const [previousMonthNotClosed, setPreviousMonthNotClosed] = useState(false)
  const [disclaimerMessage, setDisclaimerMessage] = useState('')
  const [currentBalance, setCurrentBalance] = useState('0.00')
  const [transactionSubmitted, setTransactionSubmitted] = useState(false)
  const [chartsDataLoaded, setChartsDataLoaded] = useState(false)

  // Column chooser
  const [availableColumns, setAvailableColumns] = useState(DEFAULT_COLUMNS)
  const [columnChooserPosition, setColumnChooserPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  // Time helpers
  const { isCurrentMonthLastDay, isPreviousMonth } = useTimeHelpers(selectedDate)

  // Computed
  const currentMonth = selectedDate.format('MMMM - YYYY')
  const isCurrentDatePrevMonth = isPreviousMonth()
  const discrepancyValue = (latestCashCount?.totalAmount || 0) - metricsData.systemCashOnHand

  // Aggregate expenses
  const aggregateExpensesByType = useCallback(() => {
    if (transactionsRef.current) {
      try {
        const exportData = transactionsRef.current.getExportData()
        if (exportData?.transactions) {
          const expenses = exportData.transactions
            .filter((t) => t.cashOut && t.expenseType !== 'Opening Balance')
            .reduce((acc, t) => {
              const type = t.expenseType || 'Uncategorized'
              acc[type] = (acc[type] || 0) + t.cashOut
              return acc
            }, {})
          setExpenseByType(expenses)
          setChartsDataLoaded(true)
        } else {
          setExpenseByType({})
          setChartsDataLoaded(true)
        }
      } catch (error) {
        console.error('Error in aggregateExpensesByType:', error)
        setExpenseByType({})
        setChartsDataLoaded(true)
      }
    } else {
      setExpenseByType({})
      setChartsDataLoaded(true)
    }
  }, [])

  // Button logic
  const shouldDisableButtons = useCallback(() => {
    if (!canWrite) return true
    if (previousMonthNotClosed || isCurrentDatePrevMonth) return true
    return !isCurrentDatePrevMonth && backendMonthClosedStatus
  }, [canWrite, previousMonthNotClosed, isCurrentDatePrevMonth, backendMonthClosedStatus])

  const isCloseMonthDisabled = useCallback(() => {
    if (!canCloseMonth) return true
    if (backendMonthClosedStatus) return true
    if (isCurrentDatePrevMonth) return backendMonthClosedStatus
    return !isCurrentMonthLastDay()
  }, [canCloseMonth, backendMonthClosedStatus, isCurrentDatePrevMonth, isCurrentMonthLastDay])

  const getButtonTitle = (type) => {
    const titles = {
      transaction: {
        noPermission: 'You do not have permission to add transactions',
        previousMonth: 'Cannot add transactions - previous month cash balance is not closed',
        closed: 'Cannot add transactions - month has been closed',
        prevMonthGeneral: 'Cannot add transactions to previous months',
        default: 'Add New Transaction'
      },
      cashCount: {
        noPermission: 'You do not have permission to add cash counts',
        previousMonth: 'Cannot add cash counts - previous month cash balance is not closed',
        closed: 'Cannot add cash counts - month has been closed',
        prevMonthGeneral: 'Cannot add cash counts to previous months',
        default: 'Add New Cash Count'
      },
      closeMonth: {
        noPermission: 'You do not have permission to close the month',
        closed: 'Month has already been closed',
        notLastDay: `Can only close current month within the last 5 days (Manila Time: ${dayjs().tz(MANILA_TIMEZONE).format('MMM DD, YYYY HH:mm:ss')})`,
        default: 'Close month'
      }
    }

    const typeMap = titles[type]
    if (!typeMap) return ''

    if (type === 'closeMonth') {
      if (!canCloseMonth) return typeMap.noPermission
      if (backendMonthClosedStatus) return typeMap.closed
      if (!isCurrentDatePrevMonth && !isCurrentMonthLastDay()) return typeMap.notLastDay
      return typeMap.default
    }

    if (!canWrite) return typeMap.noPermission
    if (previousMonthNotClosed) return typeMap.previousMonth
    if (shouldDisableButtons()) {
      return backendMonthClosedStatus ? typeMap.closed : typeMap.prevMonthGeneral
    }
    return typeMap.default
  }

  // API
  const fetchLatestCashCount = useCallback(async () => {
    try {
      const params = {
        month: selectedDate.format('MM'),
        year: selectedDate.format('YYYY'),
        countType: 'cashCount',
        status: 'active'
      }

      const response = await fetchCashCounts(instance, params)

      if (response?.cashCountLogs?.length > 0) {
        const sortedCashCounts = [...response.cashCountLogs].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
        const latest = sortedCashCounts[0]

        setLatestCashCount(latest)
        setHasCashCountHistory(true)
        setMetricsData(prev => ({ ...prev, actualCashOnHand: latest.totalAmount || 0 }))
      } else {
        setLatestCashCount(null)
        setHasCashCountHistory(false)
      }
      setCashCountDataLoaded(true)
    } catch (error) {
      console.error('Error fetching latest cash count:', error)
      setLatestCashCount(null)
      setHasCashCountHistory(false)
      setCashCountDataLoaded(true)
    }
  }, [instance, selectedDate])

  const refreshTransactions = useCallback(async () => {
    if (transactionsRef.current?.refreshTransactions) {
      await transactionsRef.current.refreshTransactions()
    }
  }, [])

  // Event handlers
  const handleDateChange = useCallback((date) => {
    setSelectedDate(date || dayjs())
  }, [])

  const handleTransactionModal = useCallback((action) => {
    if (action === 'open') {
      const balance = transactionsRef.current?.getCurrentBalance?.() || metricsData.systemCashOnHand
      setCurrentBalance(parseFloat(balance).toFixed(2))
      setTransactionModalVisible(true)
    } else {
      setTransactionModalVisible(false)
    }
  }, [metricsData.systemCashOnHand])

  const handleCashCountModal = useCallback((action) => {
    if (action === 'open') {
      setCashCountModalVisible(true)
    } else {
      setCashCountModalVisible(false)
      if (activeTab === 'cash-count') {
        fetchLatestCashCount()
      }
    }
  }, [activeTab, fetchLatestCashCount])

  const handleCloseMonth = useCallback(async () => {
    try {
      message.loading({ content: 'Loading...', key: 'fetchingCashCount' })
      await fetchLatestCashCount()
      message.destroy('fetchingCashCount')
      setCloseMonthModalVisible(true)
    } catch (error) {
      console.error('Error fetching cash count before closing month:', error)
      message.destroy('fetchingCashCount')
      message.error('Failed to fetch latest cash count. Please try again.')
    }
  }, [fetchLatestCashCount])

  // === CRITICAL: CLOSE MONTH + REFRESH ===
  const handleConfirmCloseMonth = useCallback(async () => {
    try {
      let closingBalance

      if (latestCashCount?.totalAmount !== undefined && latestCashCount?.totalAmount !== null) {
        closingBalance = latestCashCount.totalAmount
      } else {
        closingBalance = metricsData.systemCashOnHand
      }

      const nowInManila = dayjs().tz(MANILA_TIMEZONE)
      const closeMonthData = {
        monthTobeClosed: parseInt(selectedDate.format('MM')),
        currentYear: parseInt(selectedDate.format('YYYY'))
      }

      message.loading({ content: 'Closing month...', key: 'closeMonthLoading' })
      const response = await closeMonthlyBalance(instance, closeMonthData)
      message.destroy('closeMonthLoading')

      const isSuccess = response?.success || response?.message?.toLowerCase().includes('success')

      if (isSuccess) {
        setBackendMonthClosedStatus(true)
        setMetricsData(prev => ({
          ...prev,
          openingBalance: closingBalance,
          systemCashOnHand: closingBalance,
          totalExpenses: 0,
          totalReplenishment: 0
        }))

        const balanceSource = latestCashCount?.totalAmount !== undefined && latestCashCount?.totalAmount !== null
          ? 'actual cash count'
          : 'system cash on hand (no cash count available)'

        message.success({
          content: (
            <div>
              <p><strong>{currentMonth} closed successfully!</strong></p>
              <p>Closed at Manila time: {nowInManila.format('MMM DD, YYYY HH:mm:ss')}</p>
              <p>Opening balance of ₱{formatCurrencyWithDecimals(closingBalance)} has been carried forward from {balanceSource}.</p>
            </div>
          ),
          duration: 5
        })

        setHasTransactions(false)

        // === REFRESH ENTRIES API + SWITCH MONTH ===
        setTimeout(async () => {
          try {
            if (transactionsRef.current?.refreshTransactions) {
              await transactionsRef.current.refreshTransactions()
              console.log('Entries API refreshed after month close')
            }
            setSelectedDate(prev => prev.add(1, 'month'))
          } catch (err) {
            console.error('Failed to refresh after close:', err)
            message.warning('Month closed, but failed to load next month. Please reload.')
          }
        }, 300)

      } else {
        throw new Error(response?.message || 'Failed to close month')
      }
    } catch (error) {
      console.error('Error closing month:', error)
      message.error(`Failed to close month: ${error.message}`)
    }
    setCloseMonthModalVisible(false)
  }, [
    latestCashCount,
    selectedDate,
    metricsData.systemCashOnHand,
    instance,
    currentMonth,
    transactionsRef
  ])

  const handleExport = useCallback(async ({ key }) => {
    try {
      message.loading({ content: 'Preparing export data...', key: 'exportLoading' })
      const exportData = transactionsRef.current?.getExportData()
      if (!exportData?.transactions?.length) {
        message.destroy('exportLoading')
        message.error('No transaction data available for export')
        return
      }

      let latestCashCountForExport = null
      try {
        const params = {
          month: selectedDate.format('MM'),
          year: selectedDate.format('YYYY'),
          countType: 'cashCount',
          status: 'active'
        }
        const cashCountResponse = await fetchCashCounts(instance, params)
        if (cashCountResponse?.cashCountLogs?.length > 0) {
          const sorted = [...cashCountResponse.cashCountLogs].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
          latestCashCountForExport = sorted[0]
        }
      } catch (error) {
        console.warn('Could not fetch cash count for export:', error)
      }

      const { transactions, totalTransactions, totalTransactionsCount } = exportData
      const exportTitle = `${currentStoreName} - Petty Cash Transactions - ${currentMonth}`
      const filename = `${currentStoreName.toLowerCase().replace(/\s+/g, '-')}-petty-cash-transactions-${currentMonth.replace(' - ', '-')}`
      const processedData = createExportData(transactions)

      const systemCashOnHand = metricsData.systemCashOnHand
      const actualCashOnHand = latestCashCountForExport?.totalAmount || 0
      const discrepancyValue = actualCashOnHand - systemCashOnHand

      const enhancedTotals = {
        cashOut: totalTransactions.cashOut,
        cashIn: totalTransactions.cashIn,
        runningBalance: totalTransactions.runningBalance,
        systemCashOnHand,
        actualCashOnHand,
        discrepancy: discrepancyValue,
        count: totalTransactionsCount,
        title: exportTitle,
        hasCashCount: !!latestCashCountForExport,
        cashCountDate: latestCashCountForExport?.createdAt ?
          dayjs(latestCashCountForExport.createdAt).format('MMM DD, YYYY HH:mm') : null,
        checker: latestCashCountForExport?.countedByUser || latestCashCountForExport?.createdByUser?.userName || 'N/A'
      }

      message.destroy('exportLoading')

      if (key === '1') {
        exportTransactionsAsCSV(processedData, enhancedTotals, `${filename}.csv`)
        message.success('CSV exported successfully')
      } else if (key === '2') {
        exportTransactionsAsPDF(processedData, enhancedTotals, exportTitle)
        message.success('PDF exported successfully')
      }
    } catch (error) {
      console.error('Export error:', error)
      message.destroy('exportLoading')
      message.error('Failed to export data. Please try again.')
    }
  }, [currentStoreName, currentMonth, selectedDate, instance, metricsData.systemCashOnHand])

  // Data handlers
  const handleTransactionsLoaded = useCallback((data, apiResponse) => {
    if (apiResponse?.message?.toLowerCase().includes('previous month cash balance is not closed')) {
      setPreviousMonthNotClosed(true)
      setDisclaimerMessage(apiResponse.message)
    } else {
      setPreviousMonthNotClosed(false)
      setDisclaimerMessage('')
    }

    if (data) {
      if (data.metrics) {
        setMetricsData(prev => ({
          ...prev,
          systemCashOnHand: data.metrics.systemCOH || 0,
          openingBalance: data.metrics.openingBalance || 0,
          totalExpenses: data.metrics.totalExpenses || 0,
          totalReplenishment: data.metrics.totalDeposit || 0
        }))
      }

      setBackendMonthClosedStatus(data.isCurrentMonthClosed || false)
      const transactionsCount = data.transactions?.filter(t => t.expenseType !== 'Opening Balance')?.length || 0
      setHasTransactions(transactionsCount > 0)
    } else {
      setHasTransactions(false)
      setBackendMonthClosedStatus(false)
    }
  }, [])

  const handleCashCountLoaded = useCallback((cashCountData) => {
    if (cashCountData?.length > 0) {
      const sorted = [...cashCountData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )
      const latest = sorted[0]
      setLatestCashCount(latest)
      setHasCashCountHistory(true)
      setMetricsData(prev => ({ ...prev, actualCashOnHand: latest.totalAmount || 0 }))
    } else {
      setLatestCashCount(null)
      setHasCashCountHistory(false)
      setMetricsData(prev => ({ ...prev, actualCashOnHand: 0 }))
    }
  }, [])

  const handleTransactionsApiCalled = useCallback(() => {
    // Don't update charts if a transaction was just submitted
    if (transactionSubmitted) {
      return
    }
    // Small delay to ensure data is processed
    setTimeout(() => {
      if (transactionsRef.current) {
        try {
          const { totalTransactions, apiMetrics } =
            transactionsRef.current.getExportData() || {}
          const hasFilters = transactionsRef.current.hasFiltersApplied?.()
          if (totalTransactions && !hasFilters) {
            setMetricsData(prev => ({
              ...prev,
              systemCashOnHand: apiMetrics?.systemCOH || 0,
              openingBalance: apiMetrics?.openingBalance || 0,
              totalExpenses: totalTransactions.cashOut || 0,
              totalReplenishment: totalTransactions.cashIn || 0
            }))
          }
        } catch (error) {
          console.error('Error fetching metrics from transactions:', error)
        }
      }
      // Force chart data aggregation only if not from form submission
      if (!transactionSubmitted) {
        aggregateExpensesByType()
      }
    }, 500)
  }, [aggregateExpensesByType, transactionSubmitted])

  // Column chooser
  const toggleColumn = useCallback((key) => {
    setAvailableColumns(prev =>
      prev.map(col => col.key === key ? { ...col, checked: !col.checked } : col)
    )
    message.success('Column settings updated')
  }, [])

  const toggleAllColumns = useCallback((checked) => {
    setAvailableColumns(prev => prev.map(col => ({ ...col, checked })))
    message.success('Column settings updated')
  }, [])

  const handleDragStart = useCallback((e) => {
    if (e.target.closest('.column-chooser-container')) {
      setIsDragging(true)
      const clientX = e.clientX || (e.touches?.[0].clientX)
      const clientY = e.clientY || (e.touches?.[0].clientY)
      setDragOffset({
        x: clientX - columnChooserPosition.x,
        y: clientY - columnChooserPosition.y
      })
    }
  }, [columnChooserPosition])

  const handleDrag = useCallback((e) => {
    if (isDragging) {
      e.preventDefault()
      const clientX = e.clientX || (e.touches?.[0].clientX)
      const clientY = e.clientY || (e.touches?.[0].clientY)
      setColumnChooserPosition({
        x: clientX - dragOffset.x,
        y: clientY - dragOffset.y
      })
    }
  }, [isDragging, dragOffset])

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Chart Effects
  useEffect(() => {
    let pieChart, barChart
    if (chartModalVisible && chartsDataLoaded && popupPieChartRef.current && barChartRef.current) {
      const pieCtx = popupPieChartRef.current.getContext('2d')
      const barCtx = barChartRef.current.getContext('2d')
      if (!pieCtx || !barCtx) return

      const expenseTypes = Object.keys(expenseByType)
      const expenseValues = Object.values(expenseByType)

      if (!popupPieChartRef.current.chart || !barChartRef.current.chart || transactionSubmitted) {
        if (popupPieChartRef.current.chart) popupPieChartRef.current.chart.destroy()
        if (barChartRef.current.chart) barChartRef.current.chart.destroy()

        pieChart = new Chart(pieCtx, {
          type: 'pie',
          data: {
            labels: ['Total Expenses', 'Total Deposits'],
            datasets: [{
              data: [metricsData.totalExpenses || 0, metricsData.totalReplenishment || 0],
              backgroundColor: ['#ad0270', '#001529'],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom', labels: { font: { size: 12 }, color: '#666' } },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.label}: ₱${formatCurrencyWithDecimals(context.raw || 0)}`
                }
              }
            },
            animation: { animateRotate: false }
          }
        })

        barChart = new Chart(barCtx, {
          type: 'bar',
          data: {
            labels: expenseTypes.length ? expenseTypes : ['No Data'],
            datasets: [{
              label: 'Expenses by Type',
              data: expenseValues.length ? expenseValues : [0],
              backgroundColor: '#ad0270',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `₱${formatCurrencyWithDecimals(context.raw || 0)}`
                }
              }
            },
            scales: {
              y: { beginAtZero: true, ticks: { callback: (v) => `₱${formatCurrencyWithDecimals(v)}` } },
              x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } }
            },
            animation: { animate: false }
          }
        })

        popupPieChartRef.current.chart = pieChart
        barChartRef.current.chart = barChart

        if (transactionSubmitted) setTransactionSubmitted(false)
      }
    }

    return () => {
      if (popupPieChartRef.current?.chart) { popupPieChartRef.current.chart.destroy(); delete popupPieChartRef.current.chart }
      if (barChartRef.current?.chart) { barChartRef.current.chart.destroy(); delete barChartRef.current.chart }
    }
  }, [chartModalVisible, transactionSubmitted, expenseByType, metricsData.totalExpenses, metricsData.totalReplenishment, chartsDataLoaded])

  useEffect(() => {
    let cardBarChart
    if (pieChartRef.current && chartsDataLoaded) {
      const ctx = pieChartRef.current.getContext('2d')
      if (!ctx) return

      const expenseTypes = Object.keys(expenseByType)
      const expenseValues = Object.values(expenseByType)

      if (!pieChartRef.current.chart || transactionSubmitted) {
        if (pieChartRef.current.chart) pieChartRef.current.chart.destroy()

        cardBarChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: expenseTypes.length ? expenseTypes : ['No Data'],
            datasets: [{
              label: 'Expenses by Type',
              data: expenseValues.length ? expenseValues : [0],
              backgroundColor: '#ad0270',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.label}: ₱${formatCurrencyWithDecimals(context.raw || 0)}`
                }
              }
            },
            scales: {
              x: { ticks: { display: false } },
              y: { beginAtZero: true, ticks: { callback: (v) => `₱${formatCurrencyWithDecimals(v)}` } }
            },
            animation: { animate: false }
          }
        })

        pieChartRef.current.chart = cardBarChart
        if (transactionSubmitted) setTransactionSubmitted(false)
      }
    }

    return () => {
      if (pieChartRef.current?.chart) { pieChartRef.current.chart.destroy(); delete pieChartRef.current.chart }
    }
  }, [transactionSubmitted, expenseByType, chartsDataLoaded])

  // Effects
  useEffect(() => {
    window.pettySystemCashOnHand = metricsData.systemCashOnHand
    window.dispatchEvent(new CustomEvent('systemCashOnHandUpdated', {
      detail: { systemCashOnHand: metricsData.systemCashOnHand }
    }))
  }, [metricsData.systemCashOnHand])

  useEffect(() => {
    if (activeTab === 'cash-count' && !cashCountDataLoaded) {
      fetchLatestCashCount()
    }
  }, [activeTab, selectedDate, cashCountDataLoaded, fetchLatestCashCount])

  useEffect(() => {
    const updateMetricsFromTransactions = () => {
      if (transactionsRef.current && !transactionSubmitted) {
        try {
          const { totalTransactions, apiMetrics } = transactionsRef.current.getExportData() || {}
          const hasFilters = transactionsRef.current.hasFiltersApplied?.()
          if (totalTransactions && !hasFilters) {
            setMetricsData(prev => ({
              ...prev,
              systemCashOnHand: apiMetrics?.systemCOH || 0,
              openingBalance: apiMetrics?.openingBalance || 0,
              totalExpenses: totalTransactions.cashOut || 0,
              totalReplenishment: totalTransactions.cashIn || 0
            }))
          }
        } catch (error) {
          console.error('Error fetching metrics:', error)
        }
      }
      if (!transactionSubmitted) aggregateExpensesByType()
    }
    updateMetricsFromTransactions()
  }, [aggregateExpensesByType, selectedDate, transactionSubmitted])

  useEffect(() => {
    refreshTransactions()
  }, [selectedDate, refreshTransactions])

  useEffect(() => {
    setBackendMonthClosedStatus(false)
    setPreviousMonthNotClosed(false)
    setDisclaimerMessage('')
    setHasTransactions(true)
    setHasCashCountHistory(false)
    setCashCountDataLoaded(false)
    setLatestCashCount(null)
    setChartsDataLoaded(false)

    refreshTransactions()

    if (cashCountRef.current?.refreshCashCounts) {
      cashCountRef.current.refreshCashCounts()
    }

    if (activeTab === 'cash-count') {
      fetchLatestCashCount()
    }
  }, [selectedDate, activeTab, refreshTransactions, fetchLatestCashCount])

  // Render helpers
  const renderEmptyPlaceholder = (type) => (
    <div className="no-data-placeholder">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div className="empty-description">
            <h3>No {type} History Found</h3>
            <p>There {type === 'Transaction' ? 'are no transactions' : 'is no cash count history'} for {currentMonth}.</p>
            {!isCurrentDatePrevMonth && type === 'Transaction' && (
              <p>Start adding transactions for this month.</p>
            )}
          </div>
        }
      />
    </div>
  )

  const PreviousMonthDisclaimer = () => {
    if (!previousMonthNotClosed) return null
    return (
      <div style={{
        margin: '16px 0',
        padding: '12px 16px',
        backgroundColor: '#fff2e8',
        border: '1px solid #ffcc99',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ color: '#d46b08', fontSize: '16px', fontWeight: 'bold' }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <span style={{ color: '#d46b08', fontWeight: '600', fontSize: '14px' }}>Notice: </span>
          <span style={{ color: '#d46b08', fontSize: '14px' }}>{disclaimerMessage}</span>
        </div>
      </div>
    )
  }

  // Menus
  const exportMenuItems = [
    { key: '1', label: 'Export as CSV', icon: <FileExcelOutlined /> },
    { key: '2', label: 'Export as PDF', icon: <FilePdfOutlined /> }
  ]

  const moreOptionsItems = [
    {
      key: 'column-chooser',
      label: 'Column Chooser',
      icon: <SettingOutlined />,
      onClick: () => {
        const w = window.innerWidth, h = window.innerHeight
        setColumnChooserPosition({ x: w - 350, y: h - 450 })
        setColumnChooserVisible(true)
      }
    },
    {
      key: 'deleted-logs',
      label: 'Deleted Logs',
      icon: <DeleteOutlined />,
      onClick: () => setDeletedLogsVisible(true)
    }
  ]

  // Tabs
  const tabItems = [
    {
      key: 'all',
      label: 'All Transactions',
      children: hasTransactions ? (
        <Transactions
          ref={transactionsRef}
          selectedDate={selectedDate}
          visibleColumns={[
            'srNo', 'cashOut', 'invoiceNumber', 'cashIn', 'runningBalance',
            ...availableColumns.filter(col => col.checked).map(col => col.key)
          ]}
          onDataLoaded={handleTransactionsLoaded}
          onBalanceUpdate={setCurrentBalance}
          modalVisible={transactionModalVisible}
          onTransactionDeleted={refreshTransactions}
          transactionSubmitted={transactionSubmitted}
          onTransactionRefreshed={() => setTransactionSubmitted(false)}
          onTransactionsLoaded={handleTransactionsApiCalled}
        />
      ) : renderEmptyPlaceholder('Transaction')
    },
    {
      key: 'cash-count',
      label: 'Cash Count',
      children: activeTab !== 'cash-count' ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
          Switch to this tab to load cash count data
        </div>
      ) : cashCountDataLoaded && !hasCashCountHistory ? (
        renderEmptyPlaceholder('Cash Count')
      ) : (
        <CashCount
          ref={cashCountRef}
          selectedDate={selectedDate}
          onDataLoaded={handleCashCountLoaded}
          shouldLoadData={true}
        />
      )
    }
  ]

  const renderTabBar = (props, DefaultTabBar) => (
    <div className="custom-tabs-nav">
      <DefaultTabBar {...props} />
      <div className="tab-actions">
        {activeTab === 'all' ? (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="new-transaction-btn"
              onClick={() => handleTransactionModal('open')}
              style={{ marginRight: '8px' }}
              disabled={shouldDisableButtons() || !canWrite}
              title={getButtonTitle('transaction')}
            >
              New Transaction
            </Button>
            <Dropdown menu={{ items: exportMenuItems, onClick: handleExport }} placement="bottomRight">
              <Button type="default" icon={<ExportOutlined />} className="export-btn">
                <span className="btn-text">Export</span>
              </Button>
            </Dropdown>
            <Dropdown menu={{ items: moreOptionsItems }} placement="bottomRight" trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} className="more-options-btn" />
            </Dropdown>
          </>
        ) : (
          <Button
            type="primary"
            icon={<WalletOutlined />}
            className="cash-count-btn"
            onClick={() => handleCashCountModal('open')}
            style={{ marginRight: '8px' }}
            disabled={shouldDisableButtons() || !canWrite}
            title={getButtonTitle('cashCount')}
          >
            New Cash Count
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <div className="petty-cash-container">
      {/* Header */}
      <Row className="title-container" align="middle" justify="space-between">
        <Col xs={24} md={12}>
          <Title level={2} className="main-title">Petty Cash Management</Title>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <DatePicker
            picker="month"
            className={`month-picker highlight-month-picker ${isCurrentDatePrevMonth ? 'closed-month' : ''}`}
            format="MMMM - YYYY"
            allowClear={false}
            suffixIcon={<CalendarOutlined />}
            value={selectedDate}
            onChange={handleDateChange}
            disabledDate={(current) => current && current > dayjs().endOf('month')}
            style={{ fontWeight: 600, color: '#000', width: 190 }}
          />
          <Button
            type="primary"
            danger
            className="close-month-btn"
            icon={<CloseOutlined />}
            onClick={handleCloseMonth}
            disabled={isCloseMonthDisabled()}
            title={getButtonTitle('closeMonth')}
          >
            Close Month
          </Button>
        </Col>
      </Row>

      <hr />

      {/* Metrics Cards */}
      <Row gutter={16} className="info-cards-row">
        {[
          {
            title: activeTab === 'all' ? 'System Cash on Hand' : 'Actual Cash on Hand',
            value: activeTab === 'all' ? metricsData.systemCashOnHand : (hasCashCountHistory ? latestCashCount?.totalAmount || 0 : 0),
            showLink: activeTab !== 'all',
            isActualCash: activeTab !== 'all',
            hasDiscrepancy: activeTab !== 'all' && hasCashCountHistory && discrepancyValue !== 0,
            discrepancyType: discrepancyValue > 0 ? 'surplus' : discrepancyValue < 0 ? 'shortage' : 'balanced'
          },
          { title: 'Opening Balance', value: metricsData.openingBalance },
          { title: 'Total Expenses', value: metricsData.totalExpenses, className: 'expense-card' },
          { title: 'Total Deposits', value: metricsData.totalReplenishment },
          { title: 'Expense Analysis', isChart: true }
        ].map((card, index) => (
          <Col xs={24} md={6} key={index}>
            <div
              className={`metrics-card ${card.className || ''} ${card.isActualCash && card.hasDiscrepancy ? `actual-cash-${card.discrepancyType}` : ''} ${card.isChart ? 'chart-card' : ''}`}
              onClick={card.isChart && chartsDataLoaded ? () => setChartModalVisible(true) : undefined}
              style={card.isChart && chartsDataLoaded ? { cursor: 'pointer' } : card.isChart ? { cursor: 'default' } : {}}
            >
              <div className="card-title">{card.title}</div>
              {card.isChart ? (
                chartsDataLoaded ? (
                  <canvas ref={pieChartRef} style={{ maxHeight: '80px', maxWidth: '100%' }}></canvas>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px' }}>
                    <Spin size="small" />
                  </div>
                )
              ) : (
                <>
                  <div className="card-amount">
                    <span className="currency">₱</span>
                    {formatCurrencyWithDecimals(card.value)}
                  </div>
                  {card.isActualCash && card.hasDiscrepancy && (
                    <div className="discrepancy-indicator">
                      {card.discrepancyType === 'surplus' ? (
                        <span className="surplus-text">+₱{formatCurrencyWithDecimals(Math.abs(discrepancyValue))} Surplus</span>
                      ) : (
                        <span className="shortage-text">-₱{formatCurrencyWithDecimals(Math.abs(discrepancyValue))} Shortage</span>
                      )}
                    </div>
                  )}
                  {card.showLink && (
                    <div className="view-denomination-link">
                      <a href="#" onClick={(e) => { e.preventDefault(); setDenominationModalVisible(true) }}>
                        View in Denomination
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
        ))}
      </Row>

      <PreviousMonthDisclaimer />

      <div className="transactions-section">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="petty-cash-tabs"
          renderTabBar={renderTabBar}
          items={tabItems}
        />
      </div>

      {/* Modals */}
      <Modal
        title={<span className="chart-modal-title">Cash Flow Analysis</span>}
        open={chartModalVisible}
        onCancel={() => setChartModalVisible(false)}
        footer={[
          <Button
            key="export"
            type="primary"
            icon={<FilePdfOutlined />}
            disabled={!chartsDataLoaded}
            onClick={() => {
              exportChartsAsPDF(
                popupPieChartRef.current,
                barChartRef.current,
                `${currentStoreName} - Cash Flow Analysis - ${currentMonth}`,
                `${currentStoreName.toLowerCase().replace(/\s+/g, '-')}-cash-flow-charts-${currentMonth.replace(' - ', '-')}`
              )
            }}
          >
            Export as PDF
          </Button>,
          <Button key="close" onClick={() => setChartModalVisible(false)}>Close</Button>
        ]}
        width={1000}
        className="chart-modal"
      >
        <div className="chart-container">
          <div className="chart-item pie-chart">
            <h3 className="chart-heading">Cash Flow Breakdown</h3>
            {!chartsDataLoaded ? (
              <div className="loading-state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <Spin size="large" tip="Loading chart data..." />
              </div>
            ) : (metricsData.totalExpenses > 0 || metricsData.totalReplenishment > 0) ? (
              <canvas ref={popupPieChartRef}></canvas>
            ) : (
              <div className="empty-state"><Empty description="No cash flow data available." /></div>
            )}
          </div>
          <div className="chart-item bar-chart">
            <h3 className="chart-heading">Expenses by Type</h3>
            {!chartsDataLoaded ? (
              <div className="loading-state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <Spin size="large" tip="Loading chart data..." />
              </div>
            ) : Object.keys(expenseByType).length > 0 ? (
              <canvas ref={barChartRef}></canvas>
            ) : (
              <div className="empty-state"><Empty description="No expense data available." /></div>
            )}
          </div>
        </div>
      </Modal>

      <TransactionModal
        visible={transactionModalVisible}
        onClose={() => handleTransactionModal('close')}
        currentBalance={currentBalance}
        onSuccess={async () => {
          setTransactionSubmitted(true)
          setHasTransactions(true)
          // Ensure entries reload even when the Transactions tab was previously empty
          setTimeout(() => {
            refreshTransactions()
          }, 300)
        }}
        selectedDate={selectedDate}
      />

      <CashCountModal
        visible={cashCountModalVisible}
        onClose={() => handleCashCountModal('close')}
        onSuccess={() => {
          if (cashCountRef.current?.refreshCashCounts) {
            cashCountRef.current.refreshCashCounts().then(() => {
              if (activeTab === 'cash-count') fetchLatestCashCount()
            }).catch(console.error)
          } else if (activeTab === 'cash-count') {
            fetchLatestCashCount()
          }
        }}
        selectedDate={selectedDate}
      />

      <Modal
        title="Close Month Confirmation"
        open={closeMonthModalVisible}
        onCancel={() => setCloseMonthModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCloseMonthModalVisible(false)}>Cancel</Button>,
          <Button key="confirm" type="primary" danger onClick={handleConfirmCloseMonth}>Confirm</Button>
        ]}
      >
        <div className="close-month-confirmation">
          <p>Are you sure you want to close the current month ({currentMonth})?</p>
          <p>This action will:</p>
          <ul>
            <li>Lock all transactions for the current month</li>
            <li>Set the opening balance for the next month to ₱{(() => {
              return latestCashCount?.totalAmount !== undefined && latestCashCount?.totalAmount !== null
                ? formatCurrencyWithDecimals(latestCashCount.totalAmount)
                : formatCurrencyWithDecimals(metricsData.systemCashOnHand)
            })()} <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
                ({latestCashCount?.totalAmount !== undefined && latestCashCount?.totalAmount !== null ? 'from actual cash count' : 'from system cash on hand'})
              </span>
            </li>
          </ul>

          {latestCashCount ? (
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '6px', border: '1px solid #d1d9e0' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Latest Cash Count Information:</p>
              {[
                ['Actual Cash on Hand', latestCashCount.totalAmount],
                ['System Cash on Hand', metricsData.systemCashOnHand],
                [discrepancyValue > 0 ? 'Surplus' : discrepancyValue < 0 ? 'Discrepancy' : 'Status', discrepancyValue === 0 ? 'Balanced' : Math.abs(discrepancyValue)],
                ['Count Date', dayjs(latestCashCount.createdAt).format('MMM DD, YYYY HH:mm')],
                ['Counted By', latestCashCount.countedByUser || latestCashCount.createdByUser?.userName || 'N/A'],

              ].map(([label, value], i) => (
                <p key={i} style={{ margin: '0 0 4px 0' }}>
                  <strong>{label}:</strong>
                  {label.includes('Cash') || label === 'Surplus' || label === 'Discrepancy' ? (
                    <span style={{
                      color: label === 'Surplus' ? '#52c41a' : label === 'Discrepancy' ? '#ff4d4f' : 'inherit',
                      fontWeight: label === 'Surplus' || label === 'Discrepancy' ? '600' : 'normal',
                      marginLeft: '4px'
                    }}>
                      {typeof value === 'number' ? `₱${formatCurrencyWithDecimals(value)}` : value}
                    </span>
                  ) : (
                    <span style={{ marginLeft: '4px' }}>{value}</span>
                  )}
                </p>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeaa7' }}>
              <p style={{ margin: '0', color: '#856404' }}>
                ⚠️ <strong>Notice:</strong> No cash count found. Opening balance will be ₱{formatCurrencyWithDecimals(metricsData.systemCashOnHand)}.
              </p>
            </div>
          )}

          <p style={{ marginTop: '16px' }}><strong>This action cannot be undone.</strong></p>
        </div>
      </Modal>

      <DenominationModal
        visible={denominationModalVisible}
        onClose={() => setDenominationModalVisible(false)}
        title="Cash by Denomination"
        denominationData={latestCashCount?.denominations || []}
        systemCashOnHand={metricsData.systemCashOnHand}
        actualCashOnHand={latestCashCount?.totalAmount || 0}
        showComparison={true}
      />

      {columnChooserVisible && (
        <div
          className="column-chooser-container"
          style={{
            position: 'fixed',
            top: columnChooserPosition.y,
            left: columnChooserPosition.x,
            zIndex: 1000,
            width: 300,
            backgroundColor: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleDragStart}
          onMouseMove={handleDrag}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDrag}
          onTouchEnd={handleDragEnd}
        >
          <div className="column-chooser-header" style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f6f6f6',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
          }}>
            <div>Column Chooser</div>
            <Button type="text" size="small" onClick={() => setColumnChooserVisible(false)} style={{ padding: 0 }}>✕</Button>
          </div>
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <Checkbox
                onChange={(e) => toggleAllColumns(e.target.checked)}
                checked={availableColumns.every(col => col.checked)}
                indeterminate={availableColumns.some(col => col.checked) && !availableColumns.every(col => col.checked)}
              >
                <strong>Select All</strong>
              </Checkbox>
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {availableColumns.map(col => (
                <div key={col.key} style={{ marginBottom: '8px' }}>
                  <Checkbox checked={col.checked} onChange={() => toggleColumn(col.key)}>
                    {col.title}
                  </Checkbox>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {deletedLogsVisible && (
        <DeletedTransactionsModal
          visible={deletedLogsVisible}
          onClose={() => setDeletedLogsVisible(false)}
          selectedDate={selectedDate}
        />
      )}
    </div>
  )
}

export default PettyCashManagement